var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Typography } from "@uigovpe/components";
import { useMemo } from "react";
var DONUT_COLORS = [
    "#8979FF",
    "#F46A8A",
    "#3BC4C7",
    "#F5A25D",
    "#2F5BEA",
    "#059669",
    "#7C3AED",
    "#EF4444",
];
var chartWrapStyle = {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 32,
    justifyContent: "center",
    padding: "8px 0",
};
var legendStyle = {
    display: "grid",
    gap: 12,
    listStyle: "none",
    margin: 0,
    padding: 0,
};
var CHART_SIZE = 220;
var CHART_CENTER = CHART_SIZE / 2;
var OUTER_RADIUS = 100;
var STROKE_WIDTH = 56;
var RING_RADIUS = OUTER_RADIUS - STROKE_WIDTH / 2;
var LABEL_RADIUS = RING_RADIUS;
var toRadians = function (degrees) { return (degrees * Math.PI) / 180; };
var polarToCartesian = function (centerX, centerY, radius, angleInDegrees) {
    var angleInRadians = toRadians(angleInDegrees - 90);
    return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY + radius * Math.sin(angleInRadians),
    };
};
var describeArc = function (centerX, centerY, radius, startAngle, endAngle) {
    var start = polarToCartesian(centerX, centerY, radius, endAngle);
    var end = polarToCartesian(centerX, centerY, radius, startAngle);
    var largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return [
        "M",
        start.x,
        start.y,
        "A",
        radius,
        radius,
        0,
        largeArcFlag,
        0,
        end.x,
        end.y,
    ].join(" ");
};
export var ResultsDonutChart = function (_a) {
    var slices = _a.slices;
    var total = useMemo(function () { return slices.reduce(function (sum, slice) { return sum + slice.count; }, 0); }, [slices]);
    var segments = useMemo(function () {
        if (total <= 0) {
            return [];
        }
        var cursor = 0;
        return slices
            .map(function (slice, index) {
            var ratio = slice.count / total;
            var sweep = ratio * 360;
            var startAngle = cursor;
            var endAngle = cursor + sweep;
            cursor = endAngle;
            var midAngle = startAngle + sweep / 2;
            var labelPoint = polarToCartesian(CHART_CENTER, CHART_CENTER, LABEL_RADIUS, midAngle);
            var percentage = typeof slice.percentage === "number"
                ? slice.percentage
                : Number((ratio * 100).toFixed(2));
            if (slice.count <= 0 || sweep <= 0) {
                return null;
            }
            if (sweep >= 359.99) {
                return {
                    color: DONUT_COLORS[index % DONUT_COLORS.length],
                    fullCircle: true,
                    labelPoint: labelPoint,
                    percentage: percentage,
                    value: slice.value,
                };
            }
            return {
                color: DONUT_COLORS[index % DONUT_COLORS.length],
                fullCircle: false,
                labelPoint: labelPoint,
                path: describeArc(CHART_CENTER, CHART_CENTER, RING_RADIUS, startAngle, endAngle),
                percentage: percentage,
                value: slice.value,
            };
        })
            .filter(function (segment) {
            return Boolean(segment);
        });
    }, [slices, total]);
    return (_jsxs("div", __assign({ style: chartWrapStyle }, { children: [_jsxs("div", __assign({ style: {
                    height: CHART_SIZE,
                    position: "relative",
                    width: CHART_SIZE,
                } }, { children: [_jsxs("svg", __assign({ "aria-hidden": "true", height: CHART_SIZE, viewBox: "0 0 ".concat(CHART_SIZE, " ").concat(CHART_SIZE), width: CHART_SIZE }, { children: [total <= 0 ? (_jsx("circle", { cx: CHART_CENTER, cy: CHART_CENTER, fill: "none", r: RING_RADIUS, stroke: "#E5E7EB", strokeWidth: STROKE_WIDTH })) : (segments.map(function (segment) {
                                return segment.fullCircle ? (_jsx("circle", { cx: CHART_CENTER, cy: CHART_CENTER, fill: "none", r: RING_RADIUS, stroke: segment.color, strokeWidth: STROKE_WIDTH }, segment.value)) : (_jsx("path", { d: segment.path, fill: "none", stroke: segment.color, strokeLinecap: "butt", strokeWidth: STROKE_WIDTH }, segment.value));
                            })), segments.map(function (segment) {
                                return segment.percentage >= 8 ? (_jsxs("text", __assign({ fill: "#FFFFFF", fontSize: 12, fontWeight: 700, textAnchor: "middle", x: segment.labelPoint.x, y: segment.labelPoint.y + 4 }, { children: [Number(segment.percentage).toFixed(2).replace(/\.00$/, ""), "%"] }), "label-".concat(segment.value))) : null;
                            })] })), _jsx("div", __assign({ style: {
                            alignItems: "center",
                            display: "flex",
                            flexDirection: "column",
                            inset: 0,
                            justifyContent: "center",
                            pointerEvents: "none",
                            position: "absolute",
                        } }, { children: _jsx(Typography, __assign({ fontWeight: "bold", size: "lg" }, { children: total })) }))] })), _jsx("ul", __assign({ style: legendStyle }, { children: slices.map(function (slice, index) { return (_jsxs("li", __assign({ style: {
                        alignItems: "center",
                        display: "flex",
                        gap: 8,
                    } }, { children: [_jsx("span", { style: {
                                background: DONUT_COLORS[index % DONUT_COLORS.length],
                                borderRadius: 999,
                                display: "inline-block",
                                flexShrink: 0,
                                height: 10,
                                width: 10,
                            } }), _jsx(Typography, __assign({ size: "sm" }, { children: slice.label }))] }), slice.value)); }) }))] })));
};
